import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import {
  snapYToPitch,
  pitchToY,
  pitchToStaffIndex
} from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

/* ───────────── CONSTANTS ───────────── */

const STAFF_TOP = 40;
const STAFF_HEIGHT = 80;
const LEFT_MARGIN = 100;
const SLOT_WIDTH = 80;

/* ───────────── RHYTHM → NOTE SYMBOL ───────────── */

function noteSymbolForRhythm(rhythm) {
  if (rhythm === "flag-1") return "eighth_note";
  if (rhythm === "flag-2") return "sixteenth_note";
  return "quarter_note"; // beamed notes
}

/* ───────────── LEDGER LINES ───────────── */

function LedgerLines({ pitch, clef, symbol = "quarter_note" }) {
  const NOTEHEAD_METRICS = {
    whole_note:   { width: 20, offsetX: -1.5 },
    half_note:    { width: 16.5, offsetX: -3 },
    quarter_note: { width: 17.5, offsetX: -3 }
  };

  const { width, offsetX } =
    NOTEHEAD_METRICS[symbol] ?? NOTEHEAD_METRICS.quarter_note;

  const LEDGER_PAD = 4;
  const x1 = offsetX - LEDGER_PAD;
  const x2 = offsetX + width + LEDGER_PAD;

  const index = pitchToStaffIndex(pitch, clef);
  if (index >= -1 && index <= 9) return null;

  const lines = [];

  if (index === -2) lines.push(ledger(0));
  if (index === -3) lines.push(ledger(-5));
  if (index === -4) lines.push(ledger(0), ledger(-10));
  if (index === -5) lines.push(ledger(-5), ledger(-15));

  if (index === 10) lines.push(ledger(0));
  if (index === 11) lines.push(ledger(5));
  if (index === 12) lines.push(ledger(0), ledger(10));
  if (index === 13) lines.push(ledger(5), ledger(15));

  function ledger(y) {
    return (
      <line
        key={y}
        x1={x1}
        x2={x2}
        y1={y}
        y2={y}
        stroke="#000"
        strokeWidth={1}
      />
    );
  }

  return <g>{lines}</g>;
}

function PaletteGlyph({ symbol, stemUp = true }) {
  return (
    <svg width={36} height={36} viewBox="-20 -40 60 80">
      <g transform="scale(1.2)">
        <Glyph symbol={symbol} stemUp={stemUp} />
      </g>
    </svg>
  );
}

/* ───────────── COMPONENT ───────────── */

export default function BeamingWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const notes = value.notes || {};
  const rhythm = value.rhythm ?? null;

  const [hover, setHover] = useState(null);

  function handleClick(slot) {
    if (mode !== "edit") return;

    if (notes[slot]) {
      const next = { ...value, notes: { ...notes } };
      delete next.notes[slot];
      onChange(next);
      return;
    }

    if (!hover) return;

    onChange({
      ...value,
      notes: {
        ...notes,
        [slot]: {
          pitch: hover.pitch,
          stemUp: true
        }
      }
    });
  }

  function FlagGlyphIcon({ symbol }) {
    return (
      <svg width={36} height={36} viewBox="-10 -95 80 80">
        <g transform="scale(2.7)">
          <Glyph symbol={symbol} stemUp flagOnly={true}/>
        </g>
      </svg>
    );
  }

  function toggleStem(slot) {
    const n = notes[slot];
    if (!n) return;

    onChange({
      ...value,
      notes: {
        ...notes,
        [slot]: { ...n, stemUp: !n.stemUp }
      }
    });
  }

  function setRhythm(type) {
    const beam =
      type === "beam-2" ? 2 :
      type === "beam-1" ? 1 :
      0;

    onChange({
      ...value,
      rhythm: type,
      beam
    });
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
      {/* RHYTHM SELECTOR */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {[
          ["beam-1", <BeamIcon count={1} />],
          ["beam-2", <BeamIcon count={2} />],
          ["flag-1", <FlagGlyphIcon symbol="eighth_note" />],
          ["flag-2", <FlagGlyphIcon symbol="sixteenth_note" />]
        ].map(([id, icon]) => (
          <button
            key={id}
            disabled={mode !== "edit"}
            onClick={() => setRhythm(id)}
            style={{
              padding: 6,
              borderRadius: 6,
              border: rhythm === id ? "2px solid #000" : "1px solid #ccc",
              background: "#fff"
            }}
          >
            {icon}
          </button>
        ))}
      </div>

      <svg width={300} height={220}>
        {renderStaffLines(300, STAFF_TOP)}
        {renderClef(config.clef, STAFF_TOP)}

        {[1, 2].map(slot => {
          const x = LEFT_MARGIN + (slot - 1) * SLOT_WIDTH;
          const entry = notes[slot];

          return (
            <g key={slot}>
              {/* SLOT HIT AREA */}
              <rect
                x={x}
                y={STAFF_TOP - 30}
                width={SLOT_WIDTH}
                height={STAFF_HEIGHT + 20}
                fill="transparent"
                onMouseMove={(e) => {
                  if (mode !== "edit") return;
                  const svg = e.currentTarget.ownerSVGElement;
                  const pt = svg.createSVGPoint();
                  pt.x = e.clientX;
                  pt.y = e.clientY;
                  const y =
                    pt.matrixTransform(svg.getScreenCTM().inverse()).y;

                  setHover({
                    slot,
                    pitch: snapYToPitch(y, config.clef, {
                      allowLedgerLines: true
                    })
                  });
                }}
                onMouseLeave={() => setHover(null)}
                onClick={() => handleClick(slot)}
              />

              {/* PLACED NOTE */}
              {entry && (
                <g
                  transform={`translate(
                    ${x + SLOT_WIDTH / 2},
                    ${pitchToY(entry.pitch, config.clef)}
                  )`}
                >
                  <LedgerLines pitch={entry.pitch} clef={config.clef} />
                  <Glyph
                    symbol={noteSymbolForRhythm(rhythm)}
                    stemUp={entry.stemUp}
                  />

                  {/* NOTEHEAD CLICK RECT */}
                  <rect
                    x={-4}
                    y={-9}
                    width={20}
                    height={18}
                    fill="transparent"
                    opacity={0.1}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (mode !== "edit") return;
                      const next = { ...value, notes: { ...notes } };
                      delete next.notes[slot];
                      onChange(next);
                    }}
                  />
                </g>
              )}

              {/* HOVER GHOST */}
              {hover?.slot === slot && !entry && (
                <g
                  transform={`translate(
                    ${x + SLOT_WIDTH / 2},
                    ${pitchToY(hover.pitch, config.clef)}
                  )`}
                  opacity={0.5}
                  pointerEvents="none"
                >
                  <LedgerLines pitch={hover.pitch} clef={config.clef} />
                  <Glyph
                    symbol={noteSymbolForRhythm(rhythm)}
                    fill="#999"
                  />
                </g>
              )}

              {/* STEM BUTTON */}
              {entry && (
                <foreignObject
                  x={x + SLOT_WIDTH / 2 - 40}
                  y={STAFF_TOP + STAFF_HEIGHT + 40}
                  width={80}
                  height={30}
                >
                  <button
                    style={{ width: "80px", fontSize: "11px" }}
                    onClick={(e) => {
                      if (mode !== "edit") return;
                      e.stopPropagation();
                      toggleStem(slot);
                    }}
                  >
                    {entry.stemUp ? "stem up" : "stem down"}
                  </button>
                </foreignObject>
              )}
            </g>
          );
        })}

        {/* BEAMS — ORIGINAL LOGIC */}
        {rhythm?.startsWith("beam") && notes[1] && notes[2] && (
          <Beam
            notes={[notes[1], notes[2]]}
            beamCount={value.beam}
            clef={config.clef}
          />
        )}
      </svg>
    </QuestionLayout>
  );
}

/* ───────────── BEAM (RESTORED, UNCHANGED) ───────────── */
function BeamIcon({ count }) {
  return (
    <svg width={36} height={24}>
      <line x1={8} y1={20} x2={8} y2={4} stroke="#000" strokeWidth={2} />
      <line x1={28} y1={20} x2={28} y2={4} stroke="#000" strokeWidth={2} />
      {Array.from({ length: count }).map((_, i) => (
        <rect
          key={i}
          x={8}
          y={4 + i * 6}
          width={20}
          height={3}
          fill="#000"
        />
      ))}
    </svg>
  );
}

function Beam({ notes, beamCount, clef }) {
  const STEM_LEN = 35;
  const BEAM_GAP = 6;
  const BEAM_THICK = 4;
  const STEM_X_OFFSET = 7;

  const centers = [
    LEFT_MARGIN + SLOT_WIDTH / 2,
    LEFT_MARGIN + SLOT_WIDTH * 1.5
  ];

  function stemTip(note, cx) {
    const y = pitchToY(note.pitch, clef);
    return {
      x: note.stemUp ? cx + STEM_X_OFFSET + 4 : cx + 1,
      y: note.stemUp ? y - STEM_LEN : y + STEM_LEN
    };
  }

  function beamAnchor(note, stemTipY) {
    return note.stemUp
      ? stemTipY
      : stemTipY - BEAM_THICK;
  }

  const a = stemTip(notes[0], centers[0]);
  const b = stemTip(notes[1], centers[1]);

  const aBase = beamAnchor(notes[0], a.y);
  const bBase = beamAnchor(notes[1], b.y);

  const mixed = notes[0].stemUp !== notes[1].stemUp;

  return (
    <g>
      {Array.from({ length: beamCount }).map((_, i) => {
        const li = mixed && notes[0].stemUp
          ? i
          : mixed
            ? beamCount - 1 - i
            : i;

        const ri = mixed && notes[1].stemUp
          ? i
          : mixed
            ? beamCount - 1 - i
            : i;

        const aDir = notes[0].stemUp ? 1 : -1;
        const bDir = notes[1].stemUp ? 1 : -1;

        const ay = aBase + li * BEAM_GAP * aDir;
        const by = bBase + ri * BEAM_GAP * bDir;

        return (
          <polygon
            key={i}
            points={`
              ${a.x},${ay}
              ${b.x},${by}
              ${b.x},${by + BEAM_THICK}
              ${a.x},${ay + BEAM_THICK}
            `}
            fill="#000"
          />
        );
      })}
    </g>
  );
}
